import axios from "axios";
import { get, off, onValue, ref, remove, update } from "firebase/database";
import { db } from "../lib/firebase";
import { renewToken } from "./auth";
import { getCloudFunctionUrl } from "../utils/cloudFunctions";
import { LaunchError } from "../utils/errors";

const ANNOTATIONS_PATH = "annotations";
const OWNERS_PATH = "owners";
const ANNOTATION_READ_STATE_PATH = "annotationReadState";
const ANNOTATION_READ_SUMMARY_KEY = "_summary";

export const getAnnotationsScopeID = ({ advertiserID, isAgency }) =>
  isAgency ? `${advertiserID}-agency` : advertiserID;

const getAnnotationsPath = ({ advertiserID, isAgency }) =>
  `${ANNOTATIONS_PATH}/${getAnnotationsScopeID({ advertiserID, isAgency })}`;

const getAnnotationReadStatePath = ({ ownerUID }) =>
  `${ANNOTATION_READ_STATE_PATH}/${ownerUID}`;

const isAnnotationMessageEntry = (key) => !String(key).startsWith("_");

const toAnnotationsData = (data) => {
  if (!data || typeof data !== "object") return {};

  return Object.fromEntries(
    Object.entries(data).filter(([key]) => isAnnotationMessageEntry(key))
  );
};

const toAnnotationReadStateByScope = (data) => {
  if (!data || typeof data !== "object") return {};

  return Object.fromEntries(
    Object.entries(data).filter(
      ([scopeID]) => scopeID !== ANNOTATION_READ_SUMMARY_KEY
    )
  );
};

export const getScopeHasUnread = ({
  readStateByScope,
  advertiserID,
  isAgency,
}) => {
  const scopeID = getAnnotationsScopeID({ advertiserID, isAgency });
  return Boolean(readStateByScope?.[scopeID]?.hasUnread);
};

export const getScopeUnreadCount = ({
  readStateByScope,
  advertiserID,
  isAgency,
}) => {
  const scopeID = getAnnotationsScopeID({ advertiserID, isAgency });
  return readStateByScope?.[scopeID]?.unreadCount ?? 0;
};

export const startGetOwners = () => async (dispatch) => {
  try {
    const snapshot = await get(ref(db, OWNERS_PATH));
    return snapshot.val() || {};
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startGetOwners",
        tags: { action: "annotations" },
        level: "error",
      })
    );
    return {};
  }
};

export const startListenerAnnotations =
  ({
    advertiserID,
    isAgency,
    onUpdatedDataCallback = () => {},
    onReferenceAvailable = () => {},
  }) =>
  async (dispatch) => {
    try {
      if (!advertiserID) {
        onUpdatedDataCallback({});
        return false;
      }

      const dbRef = ref(db, getAnnotationsPath({ advertiserID, isAgency }));
      onReferenceAvailable(dbRef);
      onValue(dbRef, (snapshot) => {
        onUpdatedDataCallback(toAnnotationsData(snapshot.val()));
      });

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerAnnotations",
          tags: { action: "annotations" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerAnnotations =
  ({ listenerRef } = {}) =>
  async () => {
    if (!listenerRef) return;
    await off(listenerRef);
  };

export const startListenerAnnotationReadState =
  ({
    ownerUID,
    onUpdatedDataCallback = () => {},
    onReferenceAvailable = () => {},
  }) =>
  async (dispatch) => {
    try {
      if (!ownerUID) {
        onUpdatedDataCallback({});
        return false;
      }

      const dbRef = ref(db, getAnnotationReadStatePath({ ownerUID }));
      onReferenceAvailable(dbRef);
      onValue(dbRef, (snapshot) => {
        onUpdatedDataCallback(toAnnotationReadStateByScope(snapshot.val()));
      });

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerAnnotationReadState",
          tags: { action: "annotations" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerAnnotationReadState =
  ({ listenerRef } = {}) =>
  async () => {
    if (!listenerRef) return;
    await off(listenerRef);
  };

export const startMarkAnnotationScopeAsRead =
  ({ ownerUID, advertiserID, isAgency }) =>
  async (dispatch) => {
    try {
      if (!ownerUID || !advertiserID) return false;

      const scopeID = getAnnotationsScopeID({ advertiserID, isAgency });
      const now = Date.now();

      await update(
        ref(db, `${getAnnotationReadStatePath({ ownerUID })}/${scopeID}`),
        {
          hasUnread: false,
          unreadCount: 0,
          lastReadAt: now,
          updatedAt: now,
        }
      );

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startMarkAnnotationScopeAsRead",
          context: { ownerUID, advertiserID, isAgency },
          tags: { action: "annotations" },
          level: "error",
        })
      );
      return false;
    }
  };

export const startManageAnnotation =
  ({
    advertiserID,
    text,
    files = [],
    isAgency = false,
    mentionedUserIDs = [],
    annotationID = null,
  }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      if (!token) {
        return {
          ok: false,
          message: "User not authenticated",
          code: "UNAUTHORIZED",
        };
      }

      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("manageAnnotations")}/manageAnnotations`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          advertiserID,
          text: text?.trim(),
          files,
          isAgency,
          mentionedUserIDs,
          ...(annotationID ? { annotationID } : {}),
        },
      });

      return data;
    } catch (error) {
      const errorFormatted = error?.response?.data || error;
      dispatch(
        LaunchError(errorFormatted, false, [], {
          functionName: "startManageAnnotation",
          context: { advertiserID, isAgency, annotationID },
          tags: { operation: "manage_annotation" },
        })
      );

      return {
        ok: false,
        message: errorFormatted?.message || error?.message,
        code: errorFormatted?.code,
      };
    }
  };

export const startDeleteAnnotation =
  ({ advertiserID, isAgency = false, annotationID }) =>
  async (dispatch) => {
    try {
      if (!advertiserID || !annotationID) {
        return {
          ok: false,
          message: "Missing parameters",
          code: "MISSING_PARAMETERS",
        };
      }

      const scopeID = getAnnotationsScopeID({ advertiserID, isAgency });
      await remove(ref(db, `${ANNOTATIONS_PATH}/${scopeID}/${annotationID}`));

      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startDeleteAnnotation",
          context: { advertiserID, isAgency, annotationID },
          tags: { operation: "delete_annotation" },
        })
      );

      return {
        ok: false,
        message: error?.message,
      };
    }
  };
